import axiosInstance from "@/lib/axiosInstance";
import { ProductsStore } from "@/types/productTypes";
import { create } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";
import { devtools } from 'zustand/middleware';

// Selectors for better performance
const selectProducts = (state: ProductsStore) => state.products;
const selectIsLoading = (state: ProductsStore) => state.isLoading;
const selectError = (state: ProductsStore) => state.error;

export const userProductsStore = create<ProductsStore>()(
    devtools(
        persist(
            (set, get) => ({
                products: null,
                isLoading: false,
                error: null,
                lastFetch: null,

                getProducts: async () => {
                    // Cache invalidation - only fetch if data is older than 5 minutes
                    const now = Date.now();
                    const fiveMinutes = 5 * 60 * 1000;
                    const lastFetchTime = get().lastFetch;
                    
                    // Check if we have valid cache
                    if (get().products && lastFetchTime && (now - lastFetchTime) < fiveMinutes) {
                        return;
                    }

                    try {
                        set({ isLoading: true, error: null });
                        const res = await axiosInstance.get(`/programs`);
                        
                        if (!res.data) {
                            throw new Error('No data received from server');
                        }

                        set({ 
                            products: res.data.data, 
                            isLoading: false,
                            lastFetch: now
                        });
                    } catch (error: any) {
                        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch products';
                        console.error('Error fetching products:', errorMessage);
                        set({ 
                            isLoading: false,
                            error: errorMessage,
                            products: get().products // Keep existing products on error
                        });
                    }
                },

                // Selector functions
                useProducts: () => selectProducts(get()),
                useIsLoading: () => selectIsLoading(get()),
                useError: () => selectError(get()),
            }),
            {
                name: "product-storage",
                partialize: (state) => ({ 
                    products: state.products,
                    lastFetch: state.lastFetch
                })
            } as PersistOptions<ProductsStore>
        )
    )
);
